import { useRef, useState, useEffect } from "react";
import { Mic, Square, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function VoiceRecorder({ leadId, onAdded }) {
  const [state, setState] = useState("idle"); // "idle" | "recording" | "processing"
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const mimeTypeRef = useRef("audio/webm");

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const getSupportedMimeType = () => {
    if (typeof MediaRecorder === "undefined") return "";
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/aac",
      "audio/ogg",
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return "";
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mime = getSupportedMimeType();
      mimeTypeRef.current = mime || "audio/webm";

      const options = mime ? { mimeType: mime } : {};
      const rec = new MediaRecorder(stream, options);
      mediaRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      rec.onstop = async () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }

        const audioBlob = new Blob(chunksRef.current, {
          type: mimeTypeRef.current.split(";")[0],
        });

        if (audioBlob.size < 1000) {
          toast.error("Recording was too short. Please try again.");
          setState("idle");
          setElapsed(0);
          return;
        }

        setState("processing");

        try {
          const extension = mimeTypeRef.current.includes("mp4") ? "mp4" : "webm";
          const formData = new FormData();
          formData.append("file", audioBlob, `voice_note.${extension}`);

          const { data } = await api.post(`/leads/${leadId}/voice`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          onAdded?.(data);
          toast.success("Voice note transcribed & added!");
        } catch (err) {
          toast.error(
            err?.response?.data?.detail || "Could not transcribe audio note."
          );
        } finally {
          setState("idle");
          setElapsed(0);
        }
      };

      rec.start(250); // Slice data every 250ms
      setState("recording");
      setElapsed(0);

      const startTs = Date.now();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTs) / 1000));
      }, 500);
    } catch (err) {
      console.error("Microphone access error:", err);
      toast.error("Microphone access was denied or not supported.");
      setState("idle");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
  };

  if (state === "processing") {
    return (
      <button
        type="button"
        disabled
        className="h-12 w-full rounded-2xl bg-blue-50 border border-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center gap-2 shadow-2xs"
      >
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
        <span>Transcribing & Summarizing Note…</span>
      </button>
    );
  }

  if (state === "recording") {
    return (
      <button
        type="button"
        onClick={stop}
        className="h-12 w-full rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs flex items-center justify-center gap-2 active:scale-[0.98] shadow-lg shadow-rose-600/20 transition-all cursor-pointer animate-pulse"
      >
        <Square className="h-4 w-4 fill-white" />
        <span>
          Stop Recording · {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
          {String(elapsed % 60).padStart(2, "0")}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      className="h-12 w-full rounded-2xl bg-white hover:bg-slate-50 text-slate-900 border border-slate-200/90 font-bold text-xs flex items-center justify-center gap-2 shadow-2xs hover:border-blue-300 active:scale-[0.98] transition-all cursor-pointer"
    >
      <Mic className="h-4 w-4 text-blue-600" />
      <span>Record Voice Note</span>
    </button>
  );
}